import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Toaster, toast } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import { Button, Pill } from "../../components/common/Primitives";
import api, { formatApiError } from "../../lib/api";
import { Building2, Plus, Trash2, X, Save, Search } from "lucide-react";

const LENDER_TYPES = ["NBFC", "Bank", "Fund", "Private", "Individual", "Other"];
const PRODUCT_TYPES = [
  "working_capital", "term_loan", "invoice_discounting",
  "revenue_based", "bridge", "mezzanine",
];
const SECURITY_TYPES = [
  "unsecured", "hypothecation", "immovable", "pledge",
  "personal_guarantee", "dsra", "other",
];

const empty = () => ({
  name: "", lender_type: "NBFC",
  contact_name: "", contact_email: "", contact_phone: "",
  min_ticket_inr: "", max_ticket_inr: "",
  tenure_months_min: "", tenure_months_max: "",
  product_types: [], sectors_allow: [], sectors_block: [],
  geographies: [], security_accepted: [],
  risk_appetite: "any", turnaround_days: "",
  notes: "", active: true,
});

export default function AdminLenders() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [activeOnly, setActiveOnly] = useState(false);
  const [draft, setDraft] = useState(null); // when editing / creating
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/lenders", { params: { active_only: activeOnly } });
      setRows(r.data?.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [activeOnly]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((l) =>
      (l.name || "").toLowerCase().includes(s) ||
      (l.lender_type || "").toLowerCase().includes(s) ||
      (l.contact_email || "").toLowerCase().includes(s) ||
      (l.geographies || []).join(",").toLowerCase().includes(s) ||
      (l.product_types || []).join(",").toLowerCase().includes(s)
    );
  }, [rows, q]);

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        ...draft,
        contact_email: draft.contact_email || null,
        min_ticket_inr: draft.min_ticket_inr ? Number(draft.min_ticket_inr) : null,
        max_ticket_inr: draft.max_ticket_inr ? Number(draft.max_ticket_inr) : null,
        tenure_months_min: draft.tenure_months_min ? Number(draft.tenure_months_min) : null,
        tenure_months_max: draft.tenure_months_max ? Number(draft.tenure_months_max) : null,
        turnaround_days: draft.turnaround_days ? Number(draft.turnaround_days) : null,
      };
      Object.keys(body).forEach((k) => (body[k] === "" && (body[k] = null)));
      if (!body.name || body.name.trim().length < 2) {
        toast.error("Name required."); setBusy(false); return;
      }
      if (draft.lender_id) {
        await api.patch(`/admin/lenders/${draft.lender_id}`, body);
        toast.success("Lender updated");
      } else {
        await api.post("/admin/lenders", body);
        toast.success("Lender added");
      }
      setDraft(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const deactivate = async (l) => {
    if (!window.confirm(`Deactivate '${l.name}'? Historical shortlists remain resolvable.`)) return;
    try {
      await api.delete(`/admin/lenders/${l.lender_id}`);
      toast.success("Lender deactivated");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <DashboardShell title="Lender Database" subtitle="VenturaZ Money · Admin CRUD">
      <Toaster richColors position="top-right" />
      <div className="space-y-4" data-testid="admin-lenders">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 flex-1 max-w-md">
            <Search size={14} className="text-ink-muted" />
            <input
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, type, email, sector, geography…"
              className="flex-1 px-3 py-2 border border-navy/15 rounded text-sm"
              data-testid="lender-search"
            />
            <label className="text-[11px] font-mono text-ink-muted inline-flex items-center gap-1 cursor-pointer">
              <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} data-testid="lender-active-only" />
              Active only
            </label>
          </div>
          <Button variant="gold" onClick={() => setDraft(empty())} testId="lender-new">
            <Plus size={12} className="inline mr-1" />New lender
          </Button>
        </div>

        {loading ? (
          <div className="text-sm italic text-ink-muted">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="text-sm italic text-ink-muted border border-navy/10 rounded p-6 text-center" data-testid="lenders-empty">
            No lenders match. Click &quot;New lender&quot; to add one.
          </div>
        ) : (
          <div className="border border-navy/10 rounded bg-white/60 overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-navy/[0.04] text-[10px] font-mono uppercase tracking-widest text-ink-muted">
                <tr>
                  <th className="text-left px-3 py-2">Name</th>
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-left px-3 py-2">Contact</th>
                  <th className="text-left px-3 py-2">Ticket (₹)</th>
                  <th className="text-left px-3 py-2">Products</th>
                  <th className="text-left px-3 py-2">Geo</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-right px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-navy/[0.06]">
                {filtered.map((l) => (
                  <tr key={l.lender_id} data-testid={`lender-row-${l.lender_id}`} className="hover:bg-navy/[0.02]">
                    <td className="px-3 py-2 font-heading text-ink">{l.name}</td>
                    <td className="px-3 py-2">{l.lender_type}</td>
                    <td className="px-3 py-2 font-mono text-[10px] text-ink-muted">
                      {l.contact_name || "—"}<br />
                      {l.contact_email || "—"}
                    </td>
                    <td className="px-3 py-2 font-mono text-[10px]">
                      {l.min_ticket_inr ? Number(l.min_ticket_inr).toLocaleString("en-IN") : "—"} – {l.max_ticket_inr ? Number(l.max_ticket_inr).toLocaleString("en-IN") : "—"}
                    </td>
                    <td className="px-3 py-2 text-[10px]">{(l.product_types || []).join(", ") || "—"}</td>
                    <td className="px-3 py-2 text-[10px]">{(l.geographies || []).join(", ") || "—"}</td>
                    <td className="px-3 py-2">{l.active ? <Pill tone="success">Active</Pill> : <Pill tone="muted">Inactive</Pill>}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button onClick={() => setDraft({ ...l })} className="text-navy hover:underline text-[10px]" data-testid={`lender-edit-${l.lender_id}`}>Edit</button>
                      {l.active && (
                        <button onClick={() => deactivate(l)} className="ml-2 text-rose-700 hover:underline text-[10px]" data-testid={`lender-deactivate-${l.lender_id}`}>Deactivate</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {draft && (
          <LenderEditor draft={draft} setDraft={setDraft} onSave={save} busy={busy} onCancel={() => setDraft(null)} />
        )}
      </div>
    </DashboardShell>
  );
}

function LenderEditor({ draft, setDraft, onSave, busy, onCancel }) {
  const set = (k) => (v) => setDraft((d) => ({ ...d, [k]: v }));
  const toggleArr = (key, val) => {
    const cur = new Set(draft[key] || []);
    if (cur.has(val)) cur.delete(val); else cur.add(val);
    setDraft((d) => ({ ...d, [key]: Array.from(cur) }));
  };
  const listInput = (key) => {
    const val = (draft[key] || []).join(", ");
    return (
      <input
        value={val}
        onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) }))}
        className="w-full mt-0.5 px-3 py-2 border border-navy/15 rounded text-sm"
        placeholder="Comma-separated"
        data-testid={`lender-${key}`}
      />
    );
  };
  return (
    <div className="fixed inset-0 bg-navy/40 z-50 flex items-start justify-center overflow-y-auto p-4" onClick={onCancel}>
      <div className="bg-white rounded shadow-lg max-w-3xl w-full my-6 border border-navy/15" onClick={(e) => e.stopPropagation()} data-testid="lender-editor">
        <div className="px-4 py-3 border-b border-navy/[0.08] flex items-center justify-between">
          <div>
            <div className="overline">{draft.lender_id ? "Edit lender" : "New lender"}</div>
            <div className="font-heading text-lg">{draft.name || "—"}</div>
          </div>
          <button onClick={onCancel} data-testid="lender-editor-close"><X size={18} className="text-ink-muted" /></button>
        </div>
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <F label="Name*"><input value={draft.name} onChange={(e) => set("name")(e.target.value)} className="fld" data-testid="lender-name" /></F>
          <F label="Lender type*">
            <select value={draft.lender_type} onChange={(e) => set("lender_type")(e.target.value)} className="fld bg-white" data-testid="lender-type">
              {LENDER_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </F>
          <F label="Contact name"><input value={draft.contact_name || ""} onChange={(e) => set("contact_name")(e.target.value)} className="fld" data-testid="lender-contact-name" /></F>
          <F label="Contact email"><input type="email" value={draft.contact_email || ""} onChange={(e) => set("contact_email")(e.target.value)} className="fld" data-testid="lender-contact-email" /></F>
          <F label="Contact phone"><input value={draft.contact_phone || ""} onChange={(e) => set("contact_phone")(e.target.value)} className="fld" data-testid="lender-contact-phone" /></F>
          <F label="Turnaround days"><input type="number" value={draft.turnaround_days || ""} onChange={(e) => set("turnaround_days")(e.target.value)} className="fld" data-testid="lender-turnaround" /></F>
          <F label="Min ticket (INR)"><input type="number" value={draft.min_ticket_inr || ""} onChange={(e) => set("min_ticket_inr")(e.target.value)} className="fld" data-testid="lender-min-ticket" /></F>
          <F label="Max ticket (INR)"><input type="number" value={draft.max_ticket_inr || ""} onChange={(e) => set("max_ticket_inr")(e.target.value)} className="fld" data-testid="lender-max-ticket" /></F>
          <F label="Tenure min (months)"><input type="number" value={draft.tenure_months_min || ""} onChange={(e) => set("tenure_months_min")(e.target.value)} className="fld" data-testid="lender-tenure-min" /></F>
          <F label="Tenure max (months)"><input type="number" value={draft.tenure_months_max || ""} onChange={(e) => set("tenure_months_max")(e.target.value)} className="fld" data-testid="lender-tenure-max" /></F>
          <F label="Risk appetite">
            <select value={draft.risk_appetite || "any"} onChange={(e) => set("risk_appetite")(e.target.value)} className="fld bg-white" data-testid="lender-risk">
              {["any","senior","junior","mezzanine"].map((t) => <option key={t}>{t}</option>)}
            </select>
          </F>
          <F label="Active">
            <label className="inline-flex items-center gap-2 text-xs cursor-pointer mt-2">
              <input type="checkbox" checked={!!draft.active} onChange={(e) => set("active")(e.target.checked)} data-testid="lender-active" />
              Active for shortlisting
            </label>
          </F>

          <div className="md:col-span-2">
            <F label="Product types">
              <div className="flex flex-wrap gap-1 mt-1">
                {PRODUCT_TYPES.map((p) => {
                  const on = (draft.product_types || []).includes(p);
                  return (
                    <button key={p} onClick={() => toggleArr("product_types", p)} type="button" data-testid={`lender-prod-${p}`}
                      className={`text-[10px] font-mono px-2 py-1 rounded border ${on ? "bg-gold/25 border-gold/50 text-[#8A6600]" : "bg-navy/[0.03] border-navy/15 text-ink-muted hover:bg-navy/10"}`}>
                      {p}
                    </button>
                  );
                })}
              </div>
            </F>
          </div>
          <div className="md:col-span-2">
            <F label="Security accepted">
              <div className="flex flex-wrap gap-1 mt-1">
                {SECURITY_TYPES.map((p) => {
                  const on = (draft.security_accepted || []).includes(p);
                  return (
                    <button key={p} onClick={() => toggleArr("security_accepted", p)} type="button" data-testid={`lender-sec-${p}`}
                      className={`text-[10px] font-mono px-2 py-1 rounded border ${on ? "bg-navy/15 border-navy/40 text-navy" : "bg-navy/[0.03] border-navy/15 text-ink-muted hover:bg-navy/10"}`}>
                      {p}
                    </button>
                  );
                })}
              </div>
            </F>
          </div>

          <div className="md:col-span-2"><F label="Geographies (comma-sep)">{listInput("geographies")}</F></div>
          <div className="md:col-span-2"><F label="Sectors — allow (comma-sep)">{listInput("sectors_allow")}</F></div>
          <div className="md:col-span-2"><F label="Sectors — block (comma-sep)">{listInput("sectors_block")}</F></div>
          <div className="md:col-span-2">
            <F label="Notes">
              <textarea rows={3} value={draft.notes || ""} onChange={(e) => set("notes")(e.target.value)} className="fld" data-testid="lender-notes" />
            </F>
          </div>
        </div>
        <div className="px-4 py-3 border-t border-navy/[0.08] flex items-center justify-end gap-2">
          <button onClick={onCancel} className="text-xs text-ink-muted hover:text-ink px-3 py-2" data-testid="lender-cancel">Cancel</button>
          <Button variant="gold" onClick={onSave} disabled={busy} testId="lender-save">
            <Save size={12} className="inline mr-1" />
            {busy ? "Saving…" : (draft.lender_id ? "Update lender" : "Create lender")}
          </Button>
        </div>
      </div>
      <style>{`.fld{width:100%;margin-top:2px;padding:8px 12px;border:1px solid rgba(15,29,64,0.15);border-radius:4px;font-size:14px}`}</style>
    </div>
  );
}
function F({ label, children }) {
  return (
    <div>
      <label className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">{label}</label>
      {children}
    </div>
  );
}
